import {Editor} from '@tiptap/react';
import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {ToolbarButton} from '../../../../../../shared/ui/src/components/buttons/toolbarButton';
import {IconName} from '../../../../../../shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Types.
 */

export enum SimpleButtonsEnum {
  BOLD = 'bold',
  ITALIC = 'italic',
  UNDERLINE = 'underline',
  STRIKE = 'strike',
  BLOCKQUOTE = 'blockquote',
}

type SimpleButtonControls = {
  [key in SimpleButtonsEnum]: {
    iconName: IconName;
    label: ReactNode;
    onClick: HandlerOf<Editor>;
  };
};

/*
 * Constants.
 */

const SimpleButtonControls: SimpleButtonControls = {
  [SimpleButtonsEnum.BOLD]: {
    iconName: 'formattingBold',
    label: <Trans id="knowledge-base-editor-tool-bold">Bold</Trans>,
    onClick: (editor) => editor.chain().focus().toggleBold().run(),
  },
  [SimpleButtonsEnum.ITALIC]: {
    iconName: 'formattingItalic',
    label: <Trans id="knowledge-base-editor-tool-italic">Italic</Trans>,
    onClick: (editor) => editor.chain().focus().toggleItalic().run(),
  },
  [SimpleButtonsEnum.UNDERLINE]: {
    iconName: 'formattingUnderline',
    label: <Trans id="knowledge-base-editor-tool-underline">Underline</Trans>,
    onClick: (editor) => editor.chain().focus().toggleUnderline().run(),
  },
  [SimpleButtonsEnum.STRIKE]: {
    iconName: 'formattingStrikethrough',
    label: <Trans id="knowledge-base-editor-tool-strikethrough">Strikethrough</Trans>,
    onClick: (editor) => editor.chain().focus().toggleStrike().run(),
  },
  [SimpleButtonsEnum.BLOCKQUOTE]: {
    iconName: 'formattingBlockquote',
    label: <Trans id="knowledge-base-editor-tool-blockquote">Blockquote</Trans>,
    onClick: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
};

/*
 * Props.
 */

interface SimpleButtonProps {
  editor: Editor;
  buttonType: SimpleButtonsEnum;
}

/*
 * Component.
 */

export const SimpleButton: FC<SimpleButtonProps> = ({editor, buttonType}) => {
  const theme = useUserTheme();
  const buttonControls = SimpleButtonControls[buttonType];
  return (
    <ToolbarButton
      iconName={buttonControls.iconName}
      isActive={editor.isActive(buttonType)}
      onClick={() => buttonControls.onClick(editor)}
      label={buttonControls.label}
      shouldPreventMouseDown
      color={theme.greys.shade80}
      hoverColor={theme.greys.shade80}
    />
  );
};
